/**
 * The lines of an entry, in the phone's own search index.
 *
 * docs/plans/entry-lines-and-layout.md, Decision 19, the device half of what
 * `apps/server/src/line-search.ts` does: a line never holds a document, and
 * the words of every line under an entry, at both levels, fold into that
 * entry's document. The phone and the server answer the same search with the
 * same entry, because both render with the shared `renderLineDoc` and
 * `foldLineDocs`.
 *
 * Pure: the mirror reads stay in `entry-repo.ts`, which hands in the schemas
 * and the lines it found.
 *
 * A line's schema is what makes it a line here. A lines record binds `root`
 * (Decision 3), and nothing else does. Until the device holds that schema it
 * can neither render a line's words nor tell which entry it belongs to, so the
 * line adds nothing; when the schema lands, the parent's field set moves with
 * it and the parent's documents are rebuilt.
 */
import { linesOf, type EntryValues, type RecordSchemaResponse } from '@alluzo/shared';

import { foldLineDocs, renderLineDoc, renderSearchDoc, type SearchDocument } from '@/lib/search-doc';

/** A schema by record id, as the device holds them, or null when it holds none. */
export type SchemaOf = (recordId: string) => RecordSchemaResponse | null;

/** One lines record under an entry's record, with the schema that renders its rows. */
export type LinesUnder = {
  readonly recordId: string;
  readonly schema: RecordSchemaResponse;
  /** The field that names the entry at the top of the tree. */
  readonly rootFieldId: string;
};

/** The field a lines record's rows name their root entry by, or null when the schema is no lines record's. */
export const rootFieldOf = (schema: RecordSchemaResponse): string | null => {
  const bound = schema.bindings.root;
  return bound !== undefined && 'fieldId' in bound ? bound.fieldId : null;
};

/** Whether this schema is a lines record's: its rows are lines, never documents. */
export const isLinesSchema = (schema: RecordSchemaResponse): boolean => rootFieldOf(schema) !== null;

/** The entry a line belongs to, from its values, or null when the schema or the value does not say. */
export const rootOfLine = (schema: RecordSchemaResponse, values: EntryValues): string | null => {
  const field = rootFieldOf(schema);
  if (field === null) return null;
  const held = values[field];
  return typeof held === 'string' && held !== '' ? held : null;
};

/**
 * Every lines record under a record, at both levels, that the device holds a
 * schema for. Depth-1 records first, each followed by its own depth-2 ones. A
 * lines record with no schema in hand is left out, and so are the ones below
 * it: nothing can be rendered for them yet.
 */
export const linesUnder = (schema: RecordSchemaResponse, schemaOf: SchemaOf, depth = 0): LinesUnder[] => {
  // Two levels, never more (LINES_MAX_DEPTH). A cycle in a cached schema
  // cannot walk forever.
  if (depth >= 2) return [];
  const out: LinesUnder[] = [];
  for (const field of schema.fields) {
    if (field.type !== 'lines') continue;
    const spec = linesOf(field.spec);
    if (spec === null) continue;
    const lines = schemaOf(spec.recordId);
    if (lines === null) continue;
    const rootFieldId = rootFieldOf(lines);
    if (rootFieldId === null) continue;
    out.push({ recordId: spec.recordId, schema: lines, rootFieldId });
    out.push(...linesUnder(lines, schemaOf, depth + 1));
  }
  return out;
};

/**
 * An entry's document with the words of its lines folded in. `lines` is the
 * live lines the mirror holds under this entry, each with the schema of its
 * record. No lines answers the entry's own document.
 */
export const docWithLines = (
  schema: RecordSchemaResponse,
  values: EntryValues,
  lines: readonly { readonly schema: RecordSchemaResponse; readonly values: EntryValues }[],
): SearchDocument =>
  foldLineDocs(
    renderSearchDoc(schema, values),
    lines.map((line) => renderLineDoc(line.schema, line.values)),
  );
